import React, { useContext, useEffect, useRef, useState } from 'react';
import { ServiceResponse } from '../../../app/services/shared/interfaces';
import LazyLoading from '../../../components/LazyLoading';

import {
    TransportOrderDataForm,
    TransportOrderOptionTrip,
    defaultTransportOrder
} from '../../../app/models/TransportOrder';
import useTransportOrderService from '../../../app/services/hooks/useTransportOrderService';
import { toast } from 'react-toastify';
import { AppContext } from '../../../contexts/AppContext';
import useReactConfirmAlert from '../../../hooks/useReactConfirmAlert';
import TransportOrderForm from './forms/TransportOrderForm';
import ButtonCancelForm from '../../../components/buttons/ButtonCancelForm';
import ButtonSaveForm from '../../../components/buttons/ButtonSaveForm';
import { Client } from '../../../app/models/Client';
import { RouteSegment } from '../../../app/models/RouteSegment';
import { Product } from '../../../app/models/Product';
import { Carrier } from '../../../app/models/Carrier';
import { Truck } from '../../../app/models/Truck';
import { Hopper } from '../../../app/models/Hopper';
import { Currency } from '../../../app/models/Currency';
import { Unit } from '../../../app/models/Unit';
import useRouteSegmentService from '../../../app/services/hooks/useRouteSegmentService';

interface Props {
    transportOrderId: number;
    truckId: number;

    onSaved?: () => void;
    onCancel?: () => void;
    onError?: () => void;
}

const TransportOrderEdit = ({ transportOrderId, truckId, onSaved, onCancel, onError }: Props) => {
    const { showLoading, hideLoading, changeAnimation } = useContext(AppContext);
    const {
        fetchingEditTransportOrder,
        editTransportOrder,
        fetchingUpdateTransportOrder,
        updateTransportOrder
    } = useTransportOrderService();

    const { getInverseRouteSegments, fetchingGetInverseRouteSegmentByRouteSegments } =
        useRouteSegmentService();

    const [transportOrder, setTransportOrder] = useState<TransportOrderDataForm>({
        ...defaultTransportOrder,
        client_id: undefined,
        product_id: undefined,
        currency_external_rate_id: undefined,
        unit_external_rate_id: undefined,
        currency_commission_id: undefined,
        unit_commission_id: undefined,
        truck_id: truckId
    });

    const [copyTransportOrder, setCopyTransportOrder] = useState<TransportOrderDataForm>({});

    const [clients, setClients] = useState<Client[]>([]);
    const [carriers, setCarriers] = useState<Carrier[]>([]);
    const [trucks, setTrucks] = useState<Truck[]>([]);
    const [hoppers, setHoppers] = useState<Hopper[]>([]);
    const [currenciesExternalRate, setCurrenciesExternalRate] = useState<Currency[]>([]);
    const [unitsExternalRate, setUnitsExternalRate] = useState<Unit[]>([]);
    const [currenciesCommissionRate, setCurrenciesCommissionRate] = useState<Currency[]>([]);
    const [unitsCommissionRate, setUnitsCommissionRate] = useState<Unit[]>([]);
    const [routeSegments, setRouteSegments] = useState<RouteSegment[]>([]);
    const [inverseRouteSegments, setInverseRouteSegments] = useState<RouteSegment[]>([]);
    const [products, setProducts] = useState<Product[]>([]);
    const [optionTrips, setOptionTrips] = useState<TransportOrderOptionTrip[]>([]);

    const routeSegmentIdSelected = useRef<number | undefined>(0);
    const [fetchingRouteSegment, setFetchingRouteSegment] = useState<boolean>(false);
    const [errorFields, setErrorFields] = useState<any>();
    const confirmProgramDateRef = useRef<boolean>(false);

    useEffect(() => {
        edit();
    }, []);

    const edit = () => {
        if (showLoading) showLoading('loading', 'Cargando orden de transporte...');
        editTransportOrder(transportOrderId, {
            onSuccess: (response: ServiceResponse) => {
                setClients(response.data.clients);
                setCarriers(response.data.carriers);
                setTrucks(response.data.trucks);
                setHoppers(response.data.hoppers);
                setCurrenciesExternalRate(response.data.currency_external_rates);
                setUnitsExternalRate(response.data.unit_external_rates);
                setCurrenciesCommissionRate(response.data.currency_commissions);
                setUnitsCommissionRate(response.data.unit_commissions);
                setRouteSegments(response.data.route_segments);
                setProducts(response.data.products);

                setOptionTrips(response.data.option_trips);
                setInverseRouteSegments(response.data.inverse_route_segments);

                routeSegmentIdSelected.current =
                    response.data.transport_order.route_segment_id ?? 0;

                setTransportOrder(response.data.transport_order);
                setCopyTransportOrder(response.data.transport_order);

                if (hideLoading) hideLoading();
            },
            onError: (response: ServiceResponse) => {
                if (hideLoading) hideLoading();
                toast.error(response.message);
                if (onError) onError();
            }
        });
    };

    useEffect(() => {
        // setFetchingRouteSegment(true);
        // setTimeout(() => {
        //     setFetchingRouteSegment(false);
        // }, 500);

        if (transportOrder.route_segment_id == undefined) {
            routeSegmentIdSelected.current = undefined;
        }

        if (
            transportOrder.route_segment_id &&
            transportOrder.route_segment_id != routeSegmentIdSelected.current
        ) {
            getInverseRouteSegments(transportOrder.route_segment_id, {
                onSuccess: (response: ServiceResponse) => {
                    routeSegmentIdSelected.current = transportOrder.route_segment_id ?? 0;

                    setInverseRouteSegments(response.data.inverse_route_segments);

                    setTransportOrder({
                        ...transportOrder,
                        return_route_segment_id:
                            response.data.inverse_route_segments.length > 0
                                ? response.data.inverse_route_segments[0].id
                                : 0,
                        external_rate: response.data.route_segment.external_rate,
                        currency_external_rate_id:
                            response.data.route_segment.currency_external_rate_id ?? undefined,
                        unit_external_rate_id:
                            response.data.route_segment.unit_external_rate_id ?? undefined,
                        commission: response.data.route_segment.commission ?? undefined,
                        currency_commission_id:
                            response.data.route_segment.currency_commission_id ?? undefined,
                        unit_commission_id:
                            response.data.route_segment.unit_commission_id ?? undefined
                    });
                },
                onError: (response: ServiceResponse) => {
                    setTransportOrder({
                        ...transportOrder,
                        route_segment_id: routeSegmentIdSelected.current
                    });
                }
            });
        }
    }, [transportOrder.route_segment_id]);

    useEffect(() => {
        if (transportOrder.is_type_sell == copyTransportOrder.is_type_sell) {
            if (transportOrder.is_type_sell) {
                setTransportOrder({
                    ...transportOrder,
                    client_id: copyTransportOrder.client_id,
                    product_id: copyTransportOrder.product_id
                });
            } else if (transportOrder.is_type_sell === false) {
                setTransportOrder({
                    ...transportOrder,
                    client_id: undefined,
                    product_id: undefined
                });
            }
        } else {
            if (!transportOrder.is_type_sell) {
                setTransportOrder({
                    ...transportOrder,
                    client_id: undefined,
                    product_id: undefined
                });
            }
        }
    }, [transportOrder.is_type_sell]);

    const update = () => {
        if (showLoading) showLoading('loading', 'Actualizando orden de transporte...');
        updateTransportOrder(
            transportOrderId,
            {
                ...transportOrder,
                confirm_program_date: confirmProgramDateRef.current
            },
            {
                onSuccess: (response: ServiceResponse) => {
                    if (hideLoading) hideLoading();

                    if (response.data?.validate) {
                        confirmProgramDate(response.data.message_list);
                    } else {
                        useReactConfirmAlert().successAlert({
                            title: 'Éxito',
                            message: response.message
                        });
                        toast.success(response.message, {
                            autoClose: 2500
                        });

                        confirmProgramDateRef.current = false;
                        if (onSaved) onSaved();
                    }
                },
                onError: (response: ServiceResponse) => {
                    confirmProgramDateRef.current = false;
                    if (hideLoading) hideLoading();

                    useReactConfirmAlert().errorAlert({
                        title: 'Error',
                        message: response.message
                    });
                },
                onFieldError: (errorFields: ServiceResponse) => {
                    confirmProgramDateRef.current = false;
                    if (hideLoading) hideLoading();
                    setErrorFields(errorFields.data);
                }
            }
        );
    };

    const confirmProgramDate = (message_list: []) => {
        const _text =
            'Está seguro que desea actualizar la orden de transporte con los siguientes detalles en fecha(s) de programa:';

        useReactConfirmAlert().requestConfirmation({
            title: '¿Estás seguro?',
            message: _text,
            message_list: message_list,
            buttons: {
                confirmButton: {
                    label: 'Si, confirmar',
                    onClick: () => {
                        confirmProgramDateRef.current = true;
                        update();
                    }
                },
                cancelButton: {
                    label: 'No, cancelar',
                    onClick: () => {
                        confirmProgramDateRef.current = false;
                        setTimeout(() => {
                            useReactConfirmAlert().infoAlert({
                                title: 'Cancelado',
                                message: 'La orden de transporte no se ha creado.'
                            });
                        }, 0);
                    }
                }
            },
            onClickOutsideFunction: () => {
                confirmProgramDateRef.current = false;
            }
        });
    };

    const cancel = () => {
        if (onCancel) onCancel();
    };

    return fetchingEditTransportOrder ? (
        <LazyLoading height={300} />
    ) : (
        <>
            <div className="row">
                <div className="col-12">
                    <TransportOrderForm
                        transportOrderForm={transportOrder}
                        setTransportOrderForm={setTransportOrder}
                        errorFields={errorFields}
                        clients={clients}
                        products={products}
                        route_segments={routeSegments}
                        inverseRouteSegments={inverseRouteSegments}
                        carriers={carriers}
                        trucks={trucks}
                        hoppers={hoppers}
                        currenciesExternalRate={currenciesExternalRate}
                        unitsExternalRate={unitsExternalRate}
                        currenciesCommissionRate={currenciesCommissionRate}
                        unitsCommissionRate={unitsCommissionRate}
                        optionTrips={optionTrips}
                        fetchingRouteSegment={fetchingRouteSegment}
                        isUpdate={true}
                    />
                </div>
            </div>
            <hr />
            <div className="row justify-content-end">
                <div className="col-auto">
                    <ButtonCancelForm
                        callbackCancel={cancel}
                        locked={fetchingUpdateTransportOrder}
                    />
                </div>
                <div className="col-auto">
                    <ButtonSaveForm callbackSave={update} locked={fetchingUpdateTransportOrder} />
                </div>
            </div>
        </>
    );
};

export default TransportOrderEdit;
